import { groupBy, sortBy } from 'lodash';
import {
  ChangesetReason,
  DesignChangeMap,
  DesignChangeMapModule,
  DesignChangeMapSettings,
  DesignMap,
  GenericDesignChangeItem,
  LanguageCode,
  Option
} from '../../../../types';
import { getTypedEntries } from '../../../functions';
import { designChangeMapLabel } from '../data';
import {
  BaseChangeItemConflict,
  ConflictResolution,
  DesignConflictItem,
  DesignConflictSection,
  DesignConflictSubsection
} from '../types';
import { getConflictItemChanges } from './get-changeset-rows';
import { getConflictItemIcon } from './get-conflict-item-icon';
import { getConflictItemLabel } from './get-conflict-item-label';
import { getConflictItemModel } from './get-conflict-item-model';

interface GetChangeItemConflictProps extends Omit<(
  BaseChangeItemConflict
), (
  'source' | 'target'
)> {
  source: GenericDesignChangeItem | undefined;
  target: GenericDesignChangeItem | undefined;
}

function getChangeItemConflict({
  key,
  source,
  subsection,
  target
}: GetChangeItemConflictProps): BaseChangeItemConflict | null {
  const itemsConflict = (
    source != null
    && target != null
    && (
      source.reason !== ChangesetReason.deleted
      || target.reason !== ChangesetReason.deleted
    )
  );

  return (!itemsConflict) ? null : {
    key,
    source,
    subsection,
    target
  };
}

function getChangeItemId(
  item: GenericDesignChangeItem
): string {
  if ('link' in item) {
    return item.link.id;
  } else if ('node' in item) {
    return item.node.id;
  } else if (
    item.object != null
    && typeof item.object === 'object'
    && 'id' in item.object
    && typeof item.object.id === 'string'
  ) {
    return item.object.id;
  }

  const changeString = JSON.stringify(item, null, 2);
  throw new Error(`Unable to get ID for change item:\n${changeString}`);
}

interface GetChangeModuleConflictsProps {
  baseKey: string;
  moduleName: string;
  source: DesignChangeMapModule | undefined;
  target: DesignChangeMapModule | undefined;
}

function getChangeModuleConflicts({
  baseKey,
  moduleName,
  source,
  target
}: GetChangeModuleConflictsProps): (BaseChangeItemConflict | null)[] {
  const conflicts: (BaseChangeItemConflict | null)[] = [];
  if (!source || !target) {
    return conflicts;
  }

  (Object.keys(source) as (keyof DesignChangeMapModule)[]).forEach((
    moduleKey
  ) => {
    switch (moduleKey) {
      case 'links':
      case 'localVariables':
      case 'nodes':
        source[moduleKey]?.forEach((
          sourceItemChange
        ) => {
          const itemId = getChangeItemId(sourceItemChange);
          const targetItemChange = (
            target[moduleKey] as GenericDesignChangeItem[]
          )?.find((item) => {
            return getChangeItemId(item) === itemId;
          });
          if (!targetItemChange) {
            return;
          }

          conflicts.push(getChangeItemConflict({
            key: `${baseKey}.${moduleKey}.${itemId}`,
            source: sourceItemChange,
            subsection: {
              icon: 'package',
              label: moduleName
            },
            target: targetItemChange
          }));
        });
        break;
      default: {
        const neverKey: never = moduleKey;
        throw new Error(
          `Module key '${neverKey}' is not handled by conflict logic.`
        );
      }
    }
  });

  return conflicts;
}

interface GetChangeSettingsConflictsProps {
  baseKey: string;
  source: DesignChangeMapSettings | undefined;
  target: DesignChangeMapSettings | undefined;
}

function getChangeSettingsConflicts({
  baseKey,
  source,
  target
}: GetChangeSettingsConflictsProps): (BaseChangeItemConflict | null)[] {
  const conflicts: (BaseChangeItemConflict | null)[] = [];
  if (!source || !target) {
    return conflicts;
  }

  const pushConflict = (props: GetChangeItemConflictProps) => {
    conflicts.push(getChangeItemConflict({
      ...props,
      key: `${baseKey}.${props.key}`
    }));
  };

  (Object.keys(source) as (keyof DesignChangeMapSettings)[]).forEach((
    settingsKey
  ) => {
    switch (settingsKey) {
      case 'description':
      case 'dtmf_live_agent_override_pattern':
      case 'globalErrorHandling':
      case 'globalExceptionHandling':
      case 'noInputTimeout':
      case 'omniChannel':
      case 'rootSettings':
      case 'sensitiveOverrides':
        pushConflict({
          key: settingsKey,
          source: source[settingsKey],
          target: target[settingsKey]
        });
        break;
      case 'globalLoopHandling': {
        pushConflict({
          key: `${settingsKey}.inARow`,
          source: source[settingsKey]?.inARow,
          target: target[settingsKey]?.inARow
        });
        pushConflict({
          key: `${settingsKey}.xAndOut`,
          source: source[settingsKey]?.xAndOut,
          target: target[settingsKey]?.xAndOut
        });
        break;
      }
      case 'intentHandling': {
        pushConflict({
          key: `${settingsKey}.inARowLimit`,
          source: source[settingsKey]?.inARowLimit,
          target: target[settingsKey]?.inARowLimit
        });
        pushConflict({
          key: `${settingsKey}.xAndOutLimit`,
          source: source[settingsKey]?.xAndOutLimit,
          target: target[settingsKey]?.xAndOutLimit
        });
        break;
      }
      default: {
        const neverKey: never = settingsKey;
        throw new Error(
          `Settings key '${neverKey}' is not handled by conflict logic.`
        );
      }
    }
  });

  return conflicts;
}

interface GetChangeRecordConflictsProps {
  baseKey: string;
  source: Record<string, GenericDesignChangeItem> | undefined;
  target: Record<string, GenericDesignChangeItem> | undefined;
}

function getChangeRecordConflicts({
  baseKey,
  source,
  target
}: GetChangeRecordConflictsProps): (BaseChangeItemConflict | null)[] {
  return Object.keys(source ?? {}).map((recordKey) => {
    return getChangeItemConflict({
      key: `${baseKey}.${recordKey}`,
      source: source?.[recordKey],
      target: target?.[recordKey]
    });
  });
}

export interface GetChangeMapsConflictsProps {
  languages: Option<LanguageCode>[];
  source: DesignChangeMap;
  sourceDesignMap: DesignMap;
  target: DesignChangeMap;
  targetDesignMap: DesignMap;
}

export function getChangeMapsConflicts({
  languages,
  source,
  sourceDesignMap,
  target,
  targetDesignMap
}: GetChangeMapsConflictsProps): DesignConflictSection[] {
  const conflicts: BaseChangeItemConflict[] = [];
  const pushConflict = (conflict: BaseChangeItemConflict | null) => {
    if (conflict != null) {
      conflicts.push(conflict);
    }
  };
  const pushConflicts = (conflicts: (BaseChangeItemConflict | null)[]) => {
    conflicts.forEach(pushConflict);
  };

  (Object.keys(source) as (keyof(DesignChangeMap))[]).forEach((changeKey) => {
    switch (changeKey) {
      case 'appProperties':
      case 'application-intents':
      case 'application-modules':
      case 'application-objects':
      case 'application-variables':
      case 'ccxml':
      case 'compoundIntents':
      case 'csvData':
      case 'folders':
      case 'functions':
      case 'globalIntents':
      case 'graphQLSchemas':
      case 'unitTests':
        pushConflicts(getChangeRecordConflicts({
          baseKey: changeKey,
          source: source[changeKey],
          target: target[changeKey]
        }));
        break;
      case 'applicationVersion':
      case 'intentHandling':
      case 'languages':
        pushConflict(getChangeItemConflict({
          key: changeKey,
          source: source[changeKey],
          target: target[changeKey]
        }));
        break;
      case 'fillerPrompts':
        (Object.keys(
          source.fillerPrompts ?? {}
        ) as (keyof(Required<DesignChangeMap>['fillerPrompts']))[]).forEach((
          fillerKey
        ) => {
          pushConflicts(getChangeRecordConflicts({
            baseKey: `${changeKey}.${fillerKey}`,
            source: source.fillerPrompts?.[fillerKey],
            target: target.fillerPrompts?.[fillerKey]
          }));
        });
        break;
      case 'modules': {
        getTypedEntries(source.modules ?? {}).forEach(([
          moduleId,
          sourceModuleChanges
        ]) => {
          const appModule = sourceDesignMap.modules.get(moduleId);
          let moduleName = appModule?.name;
          if (!moduleName) {
            // Handle module that is deleted.
            const moduleChange = source['application-modules']?.[moduleId];
            moduleName = moduleChange?.node.name;
          }

          pushConflicts(getChangeModuleConflicts({
            baseKey: `${changeKey}.${moduleId}`,
            moduleName: moduleName ?? moduleId,
            source: sourceModuleChanges,
            target: target.modules?.[moduleId]
          }));
        });
        break;
      }
      case 'settings': {
        pushConflicts(getChangeSettingsConflicts({
          baseKey: changeKey,
          source: source.settings,
          target: target.settings
        }));
        break;
      }
      default: {
        const neverKey: never = changeKey;
        throw new Error(`Key '${neverKey}' is not handled by conflict logic.`);
      }
    }
  });

  const items = conflicts.map((conflictItem): DesignConflictItem => {
    const {
      key,
      source,
      subsection,
      target
    } = conflictItem;
    const [designKey] = key.split('.');
    const model = getConflictItemModel({
      conflictItem,
      sourceDesignMap,
      targetDesignMap
    });

    return {
      changes: getConflictItemChanges({
        conflictItem,
        languages,
        model,
        source,
        sourceDesignMap,
        target,
        targetDesignMap
      }),
      icon: getConflictItemIcon({
        conflictItem,
        model
      }),
      key,
      label: getConflictItemLabel({
        conflictItem,
        sourceDesignMap,
        targetDesignMap
      }),
      resolution: ConflictResolution.none,
      section: designChangeMapLabel[designKey as keyof(DesignChangeMap)],
      source,
      subsection,
      target
    };
  }).filter((conflictItem) => {
    return conflictItem.changes.length > 0;
  });

  const sections = getTypedEntries(groupBy(items, (item) => item.section)).map(([
    sectionLabel,
    sectionContent
  ]): DesignConflictSection => {
    if (!sectionContent[0].subsection) {
      return {
        items: sortBy(sectionContent, (item) => item.label),
        label: sectionLabel as string
      };
    }

    const subsections = getTypedEntries(groupBy(sectionContent, (item) => {
      return item.subsection?.label;
    })).map(([, subsectionItems]): DesignConflictSubsection => {
      const { icon, label } = subsectionItems[0].subsection ?? {};

      return {
        icon,
        items: sortBy(subsectionItems, (item) => item.label),
        label: label as string
      };
    }).filter((conflictSubsection) => {
      return conflictSubsection.items.length > 0;
    });

    return {
      subsections: sortBy(subsections, (subsection) => subsection.label),
      label: sectionLabel as string
    };
  }).filter((conflictSection) => {
    return ('items' in conflictSection) ? (
      conflictSection.items.length > 0
    ) : (
      conflictSection.subsections.length > 0
    );
  });

  return sortBy(sections, (section) => section.label);
}
